<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '../../../stores/notifications'
import type { PosNotification } from '../../../stores/notifications'

defineProps<{ isOpen: boolean }>()

const emit = defineEmits<{ (e: 'close'): void }>()

const store = useNotificationsStore()
const router = useRouter()

const items = computed(() =>
  store.items.filter((n) => !n.dismissed_at).slice(0, 8),
)
const unreadCount = computed(() => store.unreadCount)

function severityClass(severity: string): string {
  switch (severity) {
    case 'urgent':
    case 'error':
      return 'bg-red-100 text-red-700 border-red-300'
    case 'warning':
      return 'bg-amber-100 text-amber-700 border-amber-300'
    case 'success':
      return 'bg-emerald-100 text-emerald-700 border-emerald-300'
    default:
      return 'bg-blue-100 text-blue-700 border-blue-300'
  }
}

function timeAgo(iso: string | null): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const diff = Math.max(0, Date.now() - t)
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

async function openNotification(n: PosNotification) {
  if (!n.read_at) await store.markRead(n.id)
  emit('close')
  if (!n.link) return
  // Deep-link convention: `sales-order:UID` opens the order in a modal
  // on the POS Screen instead of a full-page route. The POSListView is the
  // ONLY screen that watches this signal — elsewhere it's a no-op, which
  // is the right degraded behaviour for back-office surfaces.
  if (n.link.startsWith('sales-order:')) {
    store.openSalesOrder(n.link.slice('sales-order:'.length))
    return
  }
  router.push(n.link)
}

/** Explicit "View order" button shown on sales_order rows — fires the same
 *  open path the row click does. Stops propagation so the dismiss button
 *  beside it stays accurate, and the row's click handler doesn't double-fire
 *  on the same gesture. */
async function viewOrder(n: PosNotification, e: Event) {
  e.stopPropagation()
  await openNotification(n)
}

async function viewAll() {
  emit('close')
  router.push({ name: 'notifications' })
}

async function dismiss(n: PosNotification, e: Event) {
  e.stopPropagation()
  await store.dismiss(n.id)
}

async function markAllRead() {
  await store.markAllRead()
}
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-40 z-50"
    @click="$emit('close')"
  >
    <div
      class="absolute right-0 top-0 bottom-0 w-96 max-w-full bg-white dark:bg-gray-900 shadow-xl flex flex-col"
      @click.stop
    >
      <div class="p-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Notifications</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            <span v-if="unreadCount > 0" class="text-red-500 font-medium">{{ unreadCount }} unread</span>
            <span v-else>All caught up</span>
          </p>
        </div>
        <div class="flex items-center gap-1">
          <button
            v-if="unreadCount > 0"
            @click="markAllRead"
            class="text-xs text-blue-600 hover:underline px-2 py-1"
            title="Mark all as read"
          >
            Mark all read
          </button>
          <button
            @click="$emit('close')"
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div v-if="items.length === 0" class="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
          No notifications yet.
        </div>

        <div
          v-for="n in items"
          :key="n.id"
          @click="openNotification(n)"
          class="group p-4 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors"
          :class="!n.read_at ? 'bg-blue-50/40 dark:bg-blue-950/20 hover:bg-blue-50 dark:hover:bg-blue-950/40' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'"
        >
          <div class="flex items-start gap-3">
            <span
              class="inline-block w-2 h-2 mt-2 rounded-full flex-shrink-0"
              :class="!n.read_at ? 'bg-red-500' : 'bg-transparent'"
            />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span
                  v-if="n.category"
                  class="text-[10px] px-1.5 py-0.5 rounded-full border uppercase tracking-wide"
                  :class="severityClass(n.severity)"
                >
                  {{ n.category }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400 ml-auto whitespace-nowrap">
                  {{ timeAgo(n.created_at) }}
                </span>
              </div>
              <h3
                class="font-medium text-gray-800 dark:text-white text-sm leading-snug"
                :class="!n.read_at ? 'font-semibold' : ''"
              >
                {{ n.title }}
              </h3>
              <p v-if="n.body" class="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                {{ n.body }}
              </p>
              <!-- Explicit View affordance on sales-order notifications so
                   the cashier doesn't have to guess that the whole row is
                   clickable. Fires the same handler as the row click. -->
              <button
                v-if="n.category === 'sales_order'"
                @click="viewOrder(n, $event)"
                class="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
              >
                View order →
              </button>
            </div>
            <button
              @click="dismiss(n, $event)"
              class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 text-xs p-1 transition-opacity"
              title="Dismiss"
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 dark:border-gray-800 p-3">
        <button
          @click="viewAll"
          class="w-full text-center text-sm text-blue-600 hover:underline py-2"
        >
          View all notifications
        </button>
      </div>
    </div>
  </div>
</template>
